/**
 * Dashboard — campaign hub + party overview + activity feed.
 *
 * Role-adaptive: DM sees full party roster, session controls, lore draft
 * badges. Players see their own character card and a scoped activity feed.
 * World map preview sits in the right column on desktop, stacks below on
 * mobile. Activity feed stretches full-width beneath both columns.
 *
 * Data flow:
 *   - On mount, fetches session status, characters, party, audit, and (DM)
 *     lore drafts via Promise.allSettled.
 *   - Opens a CampaignSocket and listens for session/status broadcasts so
 *     the active-session indicator and connected-players list update live.
 *   - DM can start/end a session via WS actions. The resulting broadcast
 *     updates every connected dashboard.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { isAdmin } from '../lib/auth';
import { useIdentity } from '../lib/ImpersonationContext';
import {
  AuditEntry,
  AuditPage,
  CharacterBundle,
  DEFAULT_SESSION_ID,
  getAuditLog,
  getCharacters,
  getPartySnapshot,
  getSessionStatus,
  PartySnapshot,
} from '../lib/sessionApi';
import { listDrafts } from '../lib/loreApi';
import { totalHp, combinedLevel } from '../lib/character';
import type { Character } from '../lib/character';
import type { ActiveCondition } from '../lib/runtime';
import type { Wallet } from '../lib/party';
import { CampaignSocket } from '../lib/campaignSocket';
import {
  SessionActions,
  SessionServerTypes,
  type ConnectedPlayer,
  type SessionStatusPayload,
  type LogEntryPayload,
} from '../lib/sessionProtocol';
import {
  RuntimeServerTypes,
  type RuntimePatchPayload,
} from '../lib/runtimeProtocol';
import './Dashboard.css';

// Match the other pages' default map binding so the WS subscribe puts us in
// the same session fan-out group.
const DEFAULT_MAP_ID = 'local-dev';

// ── Helpers ──────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function elapsedSince(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs > 0) return `${hrs}h ${remMins}m`;
  return `${mins}m`;
}

function hpColour(ratio: number): string {
  if (ratio > 0.5) return 'var(--active)';
  if (ratio > 0.25) return 'var(--warning)';
  return 'var(--danger)';
}

function walletSummary(w: Wallet | undefined): string {
  if (!w) return '';
  const parts: string[] = [];
  if (w.pp > 0) parts.push(`${w.pp} pp`);
  if (w.gp > 0) parts.push(`${w.gp} gp`);
  if (w.sp > 0) parts.push(`${w.sp} sp`);
  if (w.cp > 0) parts.push(`${w.cp} cp`);
  return parts.length > 0 ? parts.join(', ') : '0 gp';
}

/** Compact "3/5 hit dice" style summary. */
function hitDiceSummary(spent: number | undefined, profile: Character): string {
  const max = combinedLevel(profile);
  const remaining = max - (spent ?? 0);
  return `${remaining}/${max} HD`;
}

/** Compact spell slot summary: "L1: 2 · L2: 1" showing spent counts. */
function slotSummary(
  spellSlots: Partial<Record<string, { leveled?: Record<string, number>; pactExpended?: number | null }>> | undefined,
): string {
  if (!spellSlots) return '';
  const allExpended: Record<string, number> = {};
  for (const ss of Object.values(spellSlots)) {
    if (!ss?.leveled) continue;
    for (const [lvl, exp] of Object.entries(ss.leveled)) {
      allExpended[lvl] = (allExpended[lvl] ?? 0) + (exp ?? 0);
    }
  }
  const levels = Object.keys(allExpended).filter((l) => allExpended[l] > 0).sort();
  if (levels.length === 0) return '';
  return levels.map((l) => `L${l}: ${allExpended[l]} used`).slice(0, 4).join(' \u00b7 ');
}

function feedIcon(action: string): string {
  if (action.includes('hp') || action.includes('damage') || action.includes('heal')) return '\u2764';
  if (action.includes('roll') || action.includes('dice')) return '\uD83C\uDFB2';
  if (action.includes('condition')) return '\u26A0';
  if (action.includes('slot') || action.includes('spell')) return '\u2728';
  if (action.includes('loot') || action.includes('item') || action.includes('inventory')) return '\uD83C\uDF92';
  if (action.includes('wallet') || action.includes('pool') || action.includes('deposit') || action.includes('withdraw')) return '\uD83D\uDCB0';
  if (action.includes('session/start')) return '\uD83D\uDD6F';
  if (action.includes('session/end')) return '\uD83D\uDD6F';
  if (action.includes('session')) return '\uD83D\uDD6F';
  if (action.includes('inspiration')) return '\u2B50';
  if (action.includes('resource')) return '\u26A1';
  return '\u25CF';
}

// ── Sub-components ──────────────────────────────────────────────────────

function NavCard({
  to,
  icon,
  label,
  hint,
}: {
  to: string;
  icon: string;
  label: string;
  hint: string;
}) {
  return (
    <Link to={to} className="nav-card">
      <span className="nav-card__icon">{icon}</span>
      <span className="nav-card__label">{label}</span>
      <span className="nav-card__hint">{hint}</span>
    </Link>
  );
}

function CharacterCard({
  bundle,
  isSelf,
}: {
  bundle: CharacterBundle;
  isSelf: boolean;
}) {
  const profile = bundle.profile as Character | null;
  const runtime = bundle.runtime;
  if (!profile) return null;

  const maxHp = totalHp(profile);
  const currentHp = runtime?.currentHp ?? maxHp;
  const hpRatio = maxHp > 0 ? Math.max(0, Math.min(1, currentHp / maxHp)) : 1;
  const level = combinedLevel(profile);
  const conditions = (runtime?.conditions ?? []) as ActiveCondition[];
  const inspiration = runtime?.inspiration ?? false;
  const slotsStr = slotSummary(runtime?.spellSlots as Record<string, { leveled?: Record<string, number>; pactExpended?: number | null }> | undefined);

  const initial = profile.name.charAt(0).toUpperCase();
  const classLabel = [profile.sheetA.className, profile.sheetB.className]
    .filter(Boolean)
    .join(' / ');

  return (
    <div className={`char-card${isSelf ? ' char-card--self' : ''}`}>
      <div className="char-card__avatar">{initial}</div>
      <div className="char-card__info">
        <div className="char-card__name">{profile.name}</div>
        <div className="char-card__class">
          Level {level} {classLabel}
        </div>
        {conditions.length > 0 && (
          <div className="char-card__conditions">
            {conditions.map((c, i) => (
              <span
                key={`${c.id}-${i}`}
                className={`cond-chip${c.id === 'exhaustion' ? ' cond-chip--exhaustion' : ''}`}
              >
                {c.id}
                {c.level != null && c.level > 0 ? ` ${c.level}` : ''}
              </span>
            ))}
          </div>
        )}
        {/* Expanded detail on the player's own card */}
        {isSelf && (
          <div className="char-card__detail">
            <span className="char-card__detail-item" title="Hit dice remaining">
              {hitDiceSummary(runtime?.hitDiceSpent as number | undefined, profile)}
            </span>
            {slotsStr && (
              <span className="char-card__detail-item" title="Spell slots expended">
                {slotsStr}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="char-card__stats">
        <span
          className={`char-card__inspiration${inspiration ? ' char-card__inspiration--active' : ''}`}
          title={inspiration ? 'Inspired' : 'No inspiration'}
        >
          {'\u2B50'}
        </span>
        <div className="hp-bar">
          <span className="hp-bar__label">
            {currentHp} / {maxHp}
          </span>
          <div className="hp-bar__track">
            <div
              className="hp-bar__fill"
              style={{
                width: `${hpRatio * 100}%`,
                background: hpColour(hpRatio),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FeedEntry({ entry }: { entry: AuditEntry }) {
  const isSessionEvent =
    entry.action === 'session/start' || entry.action === 'session/end';
  if (isSessionEvent) {
    return (
      <div className="feed-entry feed-entry--session">
        {entry.summary}
      </div>
    );
  }
  return (
    <div className="feed-entry">
      <span className="feed-entry__icon">{feedIcon(entry.action)}</span>
      <span className="feed-entry__body">{entry.summary}</span>
      <span className="feed-entry__ts">{relativeTime(entry.ts)}</span>
    </div>
  );
}

function ConnectedPlayersList({ players }: { players: ConnectedPlayer[] }) {
  if (players.length === 0) return null;
  return (
    <div className="dash__connected">
      {players.map((p) => (
        <span
          key={p.sub}
          className="connected-pip"
          title={`${p.username} (${p.role})`}
        >
          <span className="connected-pip__dot" />
          {p.username}
        </span>
      ))}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth();
  const { impersonating } = useIdentity();
  // Suppress DM-only UI (session controls, admin panel, level-up card) when
  // impersonating so the DM sees the dashboard as the player would.
  const admin = !impersonating && isAdmin(user);

  // Data state
  const [characters, setCharacters] = useState<CharacterBundle[]>([]);
  const [party, setParty] = useState<PartySnapshot | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [draftCount, setDraftCount] = useState(0);

  // Session state
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionStartedAt, setSessionStartedAt] = useState<string | null>(null);
  const [connectedPlayers, setConnectedPlayers] = useState<ConnectedPlayer[]>([]);
  const [sessionActionPending, setSessionActionPending] = useState(false);

  // Timer for elapsed time display
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!sessionActive || !sessionStartedAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 30_000); // update every 30s
    return () => clearInterval(id);
  }, [sessionActive, sessionStartedAt]);

  // Loading / error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingMore = useRef(false);

  // WebSocket ref
  const socketRef = useRef<CampaignSocket | null>(null);

  // ── Initial data fetch ──

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [charResult, partyResult, auditResult, statusResult] =
          await Promise.allSettled([
            getCharacters(),
            getPartySnapshot(),
            getAuditLog(DEFAULT_SESSION_ID, { limit: 25 }),
            getSessionStatus(),
          ]);

        if (cancelled) return;

        if (charResult.status === 'fulfilled') {
          setCharacters(charResult.value.characters);
        }
        if (partyResult.status === 'fulfilled') {
          setParty(partyResult.value);
        }
        if (auditResult.status === 'fulfilled') {
          setAuditEntries(auditResult.value.entries);
          setNextBefore(auditResult.value.nextBefore);
        }
        if (statusResult.status === 'fulfilled') {
          const s = statusResult.value;
          setSessionActive(s.active);
          setSessionStartedAt(s.startedAt ?? null);
          setConnectedPlayers(s.connectedPlayers);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    // DM-only: fetch draft count
    if (admin) {
      listDrafts()
        .then((resp) => {
          if (!cancelled) setDraftCount(resp.entries.length);
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [admin]);

  // ── WebSocket for live session status ──

  useEffect(() => {
    let cs: CampaignSocket | null = null;
    let cancelled = false;

    CampaignSocket.connect({
      sessionId: DEFAULT_SESSION_ID,
      mapId: DEFAULT_MAP_ID,
    })
      .then((socket) => {
        if (cancelled) {
          socket.close();
          return;
        }
        cs = socket;
        socketRef.current = socket;

        // Listen for session status broadcasts
        socket.on<SessionStatusPayload>(
          SessionServerTypes.SessionStatus,
          (payload) => {
            setSessionActive(payload.active);
            setSessionStartedAt(payload.startedAt ?? null);
            setSessionActionPending(false);
            // Re-fetch connected players on status change (a full
            // connected-players broadcast would add complexity; polling
            // the HTTP endpoint on status change is simpler and accurate).
            getSessionStatus()
              .then((s) => setConnectedPlayers(s.connectedPlayers))
              .catch(() => {});
          },
        );

        // Listen for runtime patches to keep the party roster live.
        // Each patch carries the characterId + whichever fields changed;
        // we merge them into our local character bundles so HP bars,
        // conditions, slots etc. update without a page refresh.
        socket.on<RuntimePatchPayload>(
          RuntimeServerTypes.RuntimePatch,
          (patch) => {
            setCharacters((prev) =>
              prev.map((c) => {
                if (c.id !== patch.characterId || !c.runtime) return c;
                const rt = { ...c.runtime };
                if (patch.currentHp != null) rt.currentHp = patch.currentHp;
                if (patch.tempHp != null) rt.tempHp = patch.tempHp;
                if (patch.hitDiceSpent != null) rt.hitDiceSpent = patch.hitDiceSpent;
                if (patch.inspiration != null) rt.inspiration = patch.inspiration;
                if (patch.conditions) rt.conditions = patch.conditions;
                if (patch.spellSlots) rt.spellSlots = patch.spellSlots;
                if (patch.resources) rt.resources = patch.resources;
                if (patch.inventory) rt.inventory = patch.inventory;
                rt.version = patch.version;
                rt.updatedAt = patch.updatedAt;
                return { ...c, runtime: rt };
              }),
            );
          },
        );

        // Listen for live log entries to prepend to the feed
        socket.on<LogEntryPayload>(
          SessionServerTypes.LogEntry,
          (payload) => {
            const entry: AuditEntry = {
              id: payload.id,
              ts: payload.ts,
              sessionId: DEFAULT_SESSION_ID,
              actorSub: '',
              actorUsername: payload.actorUsername,
              action: payload.expression ?? payload.kind,
              version: 0,
              summary: payload.summary,
              reason: payload.reason ?? null,
            };
            setAuditEntries((prev) => [entry, ...prev]);
          },
        );
      })
      .catch(() => {
        // WS connection failure is non-critical for the dashboard — the
        // HTTP read path is the source of truth. The status indicator
        // just won't update live.
      });

    return () => {
      cancelled = true;
      cs?.close();
      socketRef.current = null;
    };
  }, []);

  // ── Session start / end actions ──

  const handleStartSession = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || sessionActionPending) return;
    setSessionActionPending(true);
    socket.send({ action: SessionActions.Start });
  }, [sessionActionPending]);

  const handleEndSession = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || sessionActionPending) return;
    setSessionActionPending(true);
    socket.send({ action: SessionActions.End });
  }, [sessionActionPending]);

  // ── Load more audit entries ──

  const loadMoreAudit = useCallback(async () => {
    if (!nextBefore || loadingMore.current) return;
    loadingMore.current = true;
    try {
      const page: AuditPage = await getAuditLog(DEFAULT_SESSION_ID, {
        limit: 25,
        before: nextBefore,
      });
      setAuditEntries((prev) => [...prev, ...page.entries]);
      setNextBefore(page.nextBefore);
    } catch {
      // Silently ignore pagination errors
    } finally {
      loadingMore.current = false;
    }
  }, [nextBefore]);

  // ── Derive hints for nav cards ──

  const selfChar = characters.length > 0 ? characters[0] : null;
  const selfName = selfChar?.profile?.name ?? '...';
  const selfHp = selfChar?.runtime?.currentHp ?? '?';
  const selfMaxHp = selfChar?.profile ? totalHp(selfChar.profile as Character) : '?';
  const sheetHint = `${selfName} \u2014 ${selfHp}/${selfMaxHp} HP`;

  const walletHint = party?.party?.wallet
    ? walletSummary(party.party.wallet)
    : 'Party treasury';

  const loreHint =
    admin && draftCount > 0
      ? `${draftCount} unpublished draft${draftCount > 1 ? 's' : ''}`
      : 'Campaign lore & notes';

  // ── Render ──

  if (loading) {
    return (
      <div className="dash">
        <div className="dash__loading">Loading campaign data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash">
        <div className="dash__loading" style={{ color: 'var(--danger)' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dash">
      {/* ── Hero ── */}
      <div className="dash__hero">
        <h1 className="dash__title">GoobDoob</h1>
        <span
          className={`dash__session-badge${sessionActive ? ' dash__session-badge--live' : ''}`}
        >
          <span
            className={`dash__session-dot${sessionActive ? ' dash__session-dot--live' : ''}`}
          />
          {sessionActive
            ? `Session in progress${sessionStartedAt ? ` (${elapsedSince(sessionStartedAt)})` : ''}`
            : 'No active session'}
        </span>
      </div>

      {/* ── Left column ── */}
      <div className="dash__left">
        {/* Connected players */}
        <ConnectedPlayersList players={connectedPlayers} />

        {/* Nav cards */}
        <div className="dash__nav-cards">
          <NavCard to="/vtt" icon={'\uD83D\uDDFA'} label="VTT" hint="Open the virtual tabletop" />
          <NavCard to="/sheet" icon={'\uD83D\uDCDC'} label="Sheet" hint={sheetHint} />
          <NavCard to="/party" icon={'\uD83D\uDCB0'} label="Party" hint={walletHint} />
          <NavCard to="/lore" icon={'\uD83D\uDCD6'} label="Lore" hint={loreHint} />
        </div>

        {/* DM tools */}
        {admin && (
          <div className="dash__dm-tools">
            <h2>DM Tools</h2>
            <div className="dm-action-row">
              {sessionActive ? (
                <button
                  onClick={handleEndSession}
                  disabled={sessionActionPending}
                  style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
                >
                  {sessionActionPending ? 'Ending...' : 'End Session'}
                </button>
              ) : (
                <button
                  onClick={handleStartSession}
                  disabled={sessionActionPending}
                  style={{ borderColor: 'var(--active)', color: 'var(--active)' }}
                >
                  {sessionActionPending ? 'Starting...' : 'Start Session'}
                </button>
              )}
              <Link to="/admin" className="dm-draft-badge">
                Admin Panel
              </Link>
              {draftCount > 0 && (
                <Link to="/lore" className="dm-draft-badge">
                  <span className="dm-draft-badge__count">{draftCount}</span>
                  unpublished draft{draftCount > 1 ? 's' : ''}
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Party roster */}
        <div className="dash__roster">
          <h2>Party</h2>
          {characters.length === 0 ? (
            <div className="dash__empty">
              <p>No characters loaded yet.</p>
            </div>
          ) : (
            characters.map((c) => (
              <CharacterCard
                key={c.id}
                bundle={c}
                isSelf={c.id === characters[0]?.id}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Right column (map) ── */}
      <div className="dash__right">
        <div className="dash__map-frame">
          <img src="/map.jpg" alt="The World of GoobDoob" loading="lazy" />
          <div className="dash__map-overlay">
            <h3 className="dash__map-title">The World of GoobDoob</h3>
            <p className="dash__map-hint">Click to explore the world map</p>
          </div>
          <a
            href="/map"
            target="_blank"
            rel="noopener noreferrer"
            className="dash__map-link"
          >
            Open full map
          </a>
        </div>

        {admin && (
          <NavCard
            to="/levelup"
            icon={'\u2B06'}
            label="Level Up"
            hint="Manage character progression"
          />
        )}
      </div>

      {/* ── Activity feed ── */}
      <div className="dash__feed">
        <h2>Recent Activity</h2>
        {auditEntries.length === 0 ? (
          <div className="dash__empty">
            <p>No activity yet. Start a session to see the feed come alive.</p>
          </div>
        ) : (
          <div className="feed-list">
            {auditEntries.map((entry) => (
              <FeedEntry key={entry.id} entry={entry} />
            ))}
            {nextBefore && (
              <div className="feed-more">
                <button onClick={loadMoreAudit}>Load older entries</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
